import React from 'react';

interface StashitLogoProps {
  width?: number;
  height?: number;
  className?: string;
  showTagline?: boolean;
  textColor?: string;
}

const StashitLogo: React.FC<StashitLogoProps> = ({
  width = 400,
  height = 120,
  className = "",
  showTagline = true,
  textColor = "#1e3a8a" // Default Deep Blue
}) => {
  return (
    <svg
      viewBox="0 0 400 120"
      width={width}
      height={height}
      xmlns="http://www.w3.org/2000/svg"
      className={className}
    >
      <defs>
        {/* Deep Blue gradient for depth */}
        <linearGradient id="containerGradient" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" style={{ stopColor: '#1e3a8a', stopOpacity: 1 }} />
          <stop offset="100%" style={{ stopColor: '#1e40af', stopOpacity: 1 }} />
        </linearGradient>

        {/* Bright Green gradient for accents */}
        <linearGradient id="accentGradient" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" style={{ stopColor: '#22c55e', stopOpacity: 1 }} />
          <stop offset="100%" style={{ stopColor: '#16a34a', stopOpacity: 1 }} />
        </linearGradient>

        {/* Shadow filter for depth */}
        <filter id="shadow" x="-50%" y="-50%" width="200%" height="200%">
          <feDropShadow dx="2" dy="2" stdDeviation="2" floodOpacity="0.3"/>
        </filter>

        {/* Glow filter for the keyhole */}
        <filter id="glow" x="-50%" y="-50%" width="200%" height="200%">
          <feGaussianBlur stdDeviation="1" result="coloredBlur"/>
          <feMerge>
            <feMergeNode in="coloredBlur"/>
            <feMergeNode in="SourceGraphic"/>
          </feMerge>
        </filter>
      </defs>

      {/* Container/Locker Icon */}
      <g transform="translate(10, 15)">
        {/* Main container body */}
        <rect
          x="0" y="0"
          width="45" height="60"
          rx="3" ry="3"
          fill="url(#containerGradient)"
          stroke="#1e3a8a"
          strokeWidth="2"
          filter="url(#shadow)"
        />

        {/* Door opening line */}
        <line
          x1="15" y1="5"
          x2="15" y2="55"
          stroke="#f8fafc"
          strokeWidth="2"
        />

        {/* Door hinges (left side) */}
        <rect x="1" y="10" width="4" height="8" rx="2" fill="#64748b"/>
        <rect x="1" y="42" width="4" height="8" rx="2" fill="#64748b"/>

        {/* Door handle/lock area */}
        <circle
          cx="35" cy="30"
          r="6"
          fill="#e2e8f0"
          stroke="#64748b"
          strokeWidth="1"
        />

        {/* Keyhole in handle */}
        <circle cx="35" cy="28" r="2" fill="#64748b"/>
        <rect x="34" y="30" width="2" height="4" fill="#64748b"/>

        {/* Security indicator dot */}
        <circle
          cx="35" cy="45"
          r="3"
          fill="url(#accentGradient)"
          filter="url(#glow)"
        />
      </g>

      {/* StashIt Text */}
      <g transform="translate(75, 45)">
        {/* "Stash" */}
        <text
          x="0" y="0"
          fontFamily="Inter, Arial, sans-serif"
          fontSize="32"
          fontWeight="700"
          fill={textColor}
        >
          Stash
        </text>

        {/* "-" */}
        <text
          x="100" y="0"
          fontFamily="Inter, Arial, sans-serif"
          fontSize="32"
          fontWeight="700"
          fill={textColor}
        >
          -
        </text>

        {/* "i" with keyhole design */}
        <g transform="translate(115, -32)">
          {/* Keyhole body (circular top) */}
          <circle
            cx="0" cy="20"
            r="8"
            fill="url(#accentGradient)"
            stroke="#1e3a8a"
            strokeWidth="1"
            filter="url(#glow)"
          />
          {/* Keyhole slot (rectangular bottom) */}
          <rect x="-3" y="25" width="6" height="12" fill="url(#accentGradient)"/>
          {/* Inner keyhole */}
          <circle cx="0" cy="20" r="3" fill="#f8fafc"/>
          <rect x="-1.5" y="21" width="3" height="8" fill="#f8fafc"/>
        </g>

        {/* "t" */}
        <text
          x="130" y="0"
          fontFamily="Inter, Arial, sans-serif"
          fontSize="32"
          fontWeight="700"
          fill={textColor}
        >
          t
        </text>
      </g>

      {/* Tagline */}
      {showTagline && (
        <text
          x="75" y="75"
          fontFamily="Inter, Arial, sans-serif"
          fontSize="10"
          fill="#64748b"
          fontStyle="italic"
        >
          Secure • Simple • Smart Storage
        </text>
      )}

      {/* Decorative elements */}
      <g opacity="0.1">
        {/* Subtle background pattern */}
        <circle cx="350" cy="30" r="20" fill="url(#containerGradient)"/>
        <circle cx="370" cy="80" r="15" fill="url(#accentGradient)"/>
      </g>
    </svg>
  );
};

export default StashitLogo;
